/**
 * Choosing a supplier for an item the customer added after the quotation went out
 * (prototype `revSupplierModal`).
 *
 * The ranking is the whole point. A fresh RFQ costs days; a supplier we have quoted this
 * item to before — on any requirement — already has a price we can stand behind. Those are
 * listed first with that price shown, so the purchaser can decide now. Connected suppliers
 * with no history follow, because sometimes a new quote is worth the wait.
 */

import { Modal } from '../../components/Modal'
import { Badge } from '../../components/Badge'
import { Button, Placeholder } from '../../components/ui'
import { Money } from '../../components/Money'
import { formatDate } from '../../lib/format'
import { rankSuppliersByHistory } from '../../lib/revisions'
import { useItem, useItemPrices, useStock } from '../../lib/api/useItems'
import { usePurchaseHistory } from '../../lib/api/useSuppliers'
import type { ItemContract, Money as MoneyValue, RevisionRequest } from '../../lib/types'

/** The item's units — base first, then the master's conversions — plus the current choice if odd. */
function uomChoices(item: { base_uom: string; uom_conversions: { uom: string }[] } | undefined, current: string): string[] {
  const units = item ? [item.base_uom, ...item.uom_conversions.map((c) => c.uom)] : []
  return [...new Set([...units, ...(current ? [current] : [])].filter((u) => u !== ''))]
}

export function SupplierChoiceModal({
  request,
  busy,
  onPick,
  onClose,
  onSendRfq,
  onRecordQuotation,
  contracts = [],
  onPickContract,
  onPickStock,
  choice,
  onChoice,
  positions,
}: {
  request: RevisionRequest
  busy: boolean
  /**
   * The unit and serial number the added line takes (Akhil, 2026-09-15) — held by the page,
   * because every pick below queues the add with them. The unit list comes from the item's own
   * master here; the positions from the page's batch (`positionOptions`).
   */
  choice: { uom: string; position: string }
  onChoice: (patch: Partial<{ uom: string; position: string }>) => void
  positions: { value: string; label: string }[]
  onPick: (supplier: string) => void
  onClose: () => void
  /**
   * Active blanket orders covering this item, cheapest first (`buildRateIndex`).
   *
   * Shown ABOVE the suppliers because a contract rate is already agreed: there is nothing to
   * quote and nobody to wait for. Empty is the ordinary case and renders nothing.
   */
  contracts?: ItemContract[]
  /** Queue the add priced at this contract's rate. */
  onPickContract?: (contract: ItemContract) => void
  /**
   * Queue the add supplied from OUR OWN shelf, at our unit cost.
   *
   * The fourth way to price a line, alongside a contract, a past supplier price and a fresh RFQ —
   * and the one this dialog had no route to at all, so a purchaser holding twelve on the shelf
   * still had to ask somebody for a quote.
   */
  onPickStock?: (unitCost: MoneyValue) => void
  /**
   * Ask suppliers for a price on this item — for the case the whole dialog exists to expose, where
   * nobody has quoted it before. Optional: absent simply hides the action.
   */
  onSendRfq?: () => void
  /** Record a price already given by phone or mail, without waiting for an RFQ round. */
  onRecordQuotation?: () => void
}) {
  const itemQuery = useItem(request.item_code)
  // Not scoped to this requirement — a price from any past request is what makes the
  // decision possible without a new RFQ.
  const historyQuery = usePurchaseHistory(request.item_code, 24)

  /*
    Our own shelf. Two separate reads because they answer different questions: `useStock` says how
    many are free to commit (`qty - reserved`), `useItemPrices` says what a unit costs us. Offering
    the option without BOTH would either quote a price for goods we do not have or commit stock at
    a price nobody knows.
  */
  const stockQuery = useStock(request.item_code ?? '')
  const priceQuery = useItemPrices(request.item_code ? [request.item_code] : [])
  const available = stockQuery.data?.available ?? null
  // `useItemPrices` answers `{price, source}` per code — the IN-HOUSE cost where one exists.
  const ourCost = request.item_code ? priceQuery.data?.get(request.item_code)?.price : undefined
  /*
    Availability does NOT gate this (Akhil, 2026-09-07) — only a known COST does.

    It used to require `available > 0`, so an item reading `-10 PKT` could not be priced from our
    own cost at all. Negative availability is over-COMMITMENT rather than absence: the goods may be
    on order, or the reservation may be the very one being fulfilled. Taking the price commits no
    stock; whether we can supply is the purchaser's judgement, and the figure is stated beside the
    button so they can make it.
  */
  const canUseStock = !!onPickStock && !!ourCost && ourCost.amount > 0

  const options = rankSuppliersByHistory(itemQuery.data, historyQuery.data)
  const withHistory = options.filter((o) => o.hasHistory).length
  const loading = itemQuery.isPending || historyQuery.isPending

  return (
    <Modal
      title={`Add “${itemQuery.data?.name ?? request.item_code}” × ${request.qty ?? 1} — choose supplier`}
      onClose={onClose}
    >
      <div className="pad">
        {/* Unit and place first — they apply whichever price is taken below. */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <label className="ef" style={{ minWidth: 160 }}>
            <span>Unit</span>
            <select value={choice.uom} disabled={busy} onChange={(e) => onChoice({ uom: e.target.value })}>
              {choice.uom === '' ? <option value="">choose a unit…</option> : null}
              {uomChoices(itemQuery.data, choice.uom).map((u) => (
                <option key={u} value={u}>
                  {u}
                  {itemQuery.data && u === itemQuery.data.base_uom ? ' (base)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="ef" style={{ minWidth: 200 }}>
            <span>Line number</span>
            <select value={choice.position} disabled={busy} onChange={(e) => onChoice({ position: e.target.value })}>
              {positions.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="note">
          {loading ? (
            'Looking up what we have quoted for this item before…'
          ) : canUseStock ? (
            /*
              Said first when it applies, for the same reason the contract line is: the message
              below is about the SUPPLIER list, and "picking one requests a fresh quote" is the
              wrong answer for an item sitting on our own shelf.
            */
            <>
              {available !== null && available > 0 ? (
                <>
                  We have <b>{available}</b> of this on the shelf — supply it from stock and skip the
                  RFQ entirely.
                </>
              ) : (
                <>
                  We hold a <b>cost</b> for this item, so you can price it from our own stock without
                  an RFQ — though none is free to commit right now.
                </>
              )}{' '}
              The suppliers below are the alternative.
            </>
          ) : contracts.length > 0 ? (
            /*
              Said FIRST when it applies. The message below is "we have no past price… picking a
              supplier requests a fresh quote", which is true of the supplier list but reads as the
              whole answer — and it is the wrong answer for an item whose rate is already fixed.
            */
            <>
              This item is on <b>{contracts.length} active contract{contracts.length === 1 ? '' : 's'}</b>, so its
              rate is already agreed — take it below and skip the RFQ. The suppliers after it are the
              alternative, if you want a fresh quote instead.
            </>
          ) : withHistory > 0 ? (
            <>
              We've RFQ'd this item on <b>other requests</b> before, so <b>{withHistory} of {options.length}</b>{' '}
              connected suppliers have a <b>past price</b> (shown) — you can decide without waiting for a new quote.
              The rest are suppliers linked to this item with no history yet.
            </>
          ) : (
            <>
              We have <b>no past price</b> for this item from any supplier, so these are the connected suppliers —
              picking one requests a fresh quote.
            </>
          )}
        </div>
      </div>

      {/*
        OUR OWN SHELF, first of all.

        There is nothing to ask and nobody to wait for: the goods are here and the cost is known.
        A contract is next (agreed rate, but the supplier still has to deliver), then a past price,
        then a fresh RFQ. The dialog had no route to this at all, so a purchaser holding twelve on
        the shelf still had to go and get a quote.
      */}
      {canUseStock && ourCost ? (
        <div className="mb">
          <div className="pad" style={{ borderTop: '1px solid var(--line)', paddingBottom: 4 }}>
            <b>📦 From our own stock</b>{' '}
            <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
              — on the shelf now. No RFQ, no waiting.
            </span>
          </div>
          <div
            className="pad supcheck"
            style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: busy ? 'default' : 'pointer' }}
            onClick={() => !busy && onPickStock?.(ourCost)}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>In-house stock</div>
              {/*
                Stated plainly, and never as a reason the button is off — it is not. Three real
                states: enough on the shelf, some but not enough, and none free at all (which shows
                NEGATIVE when we are over-committed, the case that used to block this entirely).
              */}
              <div style={{ color: 'var(--muted)', fontSize: 11 }}>
                {available === null ? (
                  'stock not known for this item'
                ) : available <= 0 ? (
                  <span style={{ color: 'var(--warn)' }}>none free to commit ({available}) — check you can supply it</span>
                ) : request.qty && request.qty > available ? (
                  <span style={{ color: 'var(--warn)' }}>
                    {available} available · fewer than the {request.qty} asked for
                  </span>
                ) : (
                  `${available} available`
                )}
              </div>
            </span>
            <Badge tone="teal">stock</Badge>
            <span className="mono">
              <Money value={ourCost} />
            </span>
            <Button size="sm" variant="primary" disabled={busy} onClick={() => onPickStock?.(ourCost)}>
              Use our stock
            </Button>
          </div>
        </div>
      ) : null}

      {/*
        CONTRACTS FIRST. A blanket order fixes the rate with a named supplier in advance, so a
        covered item has nothing to compete — asking for quotes on an agreed price wastes days.
        This screen used to offer only past prices and a fresh RFQ, so a contract-covered item
        added mid-revision reached the quotation at 0.00 with its rate sitting unread (measured:
        VEG EGGPLANT at AED 4.85 on Fruits Garden Trading LLC's contract).
      */}
      {contracts.length > 0 && onPickContract ? (
        <div className="mb">
          <div className="pad" style={{ borderTop: '1px solid var(--line)', paddingBottom: 4 }}>
            <b>📄 On contract</b>{' '}
            <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
              — the rate is already agreed. No RFQ, no waiting.
            </span>
          </div>
          {contracts.map((contract) => (
            <div
              key={contract.id}
              className="pad supcheck"
              style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: busy ? 'default' : 'pointer' }}
              onClick={() => !busy && onPickContract(contract)}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{contract.supplier}</div>
                <div style={{ color: 'var(--muted)', fontSize: 11 }}>
                  {contract.id}
                  {contract.valid_till ? <> · valid to {formatDate(contract.valid_till)}</> : null}
                </div>
              </span>
              <Badge tone="violet">contract</Badge>
              <span className="mono">
                <Money value={contract.rate} />
              </span>
              <Button size="sm" variant="primary" disabled={busy} onClick={() => onPickContract(contract)}>
                Use this rate
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mb">
        {loading ? <Placeholder>Loading supplier history…</Placeholder> : null}
        {!loading && options.length === 0 ? (
          <div className="pad" style={{ color: 'var(--muted)' }}>
            No suppliers are linked to this item.
          </div>
        ) : null}

        {/*
          The two ways forward when history cannot answer.

          This dialog's premise is "somebody has quoted this before, so decide now". When nobody
          has — no options at all, or none with history — that premise fails and the purchaser was
          left with a list they could not act on and no route out except abandoning the revision.

          Both actions are shown together because they are the same decision at different speeds:
          ask and wait, or record a price already in hand. Offered whenever no option carries
          history, not only when the list is empty — a supplier linked to the item but who has
          never priced it is no more use here than no supplier at all.
        */}
        {/*
          Offered even when a CONTRACT covers the item. A contracted rate is the usual answer but
          not the only one — a purchaser may still want a fresh quote, or have one in hand — so the
          route out stays available and the choice stays theirs. Only the caption changes.
        */}
        {/*
          ALWAYS offered (Akhil, 2026-09-07) — not only when no supplier has history.

          These used to appear solely when `withHistory === 0`, on the premise that a past price
          answers the question. It does not: a purchaser looking at a price from August may well
          want a fresh quote, or already have one on the phone. Hiding the route because an old
          number exists made the dialog decide for them.

          The full set is now always reachable: our own stock, a contract, any past price, a fresh
          RFQ, or a price recorded by hand.
        */}
        {!loading && (onSendRfq || onRecordQuotation) ? (
          <div
            className="pad"
            style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid var(--line)' }}
          >
            <span style={{ fontSize: 12.5, color: 'var(--muted)', flex: '1 1 200px' }}>
              {/* "No past price" is false once a contract is listed above — the rate is agreed,
                  it simply did not come from a past quotation. */}
              {contracts.length > 0
                ? 'Want a quote instead of the contract rate?'
                : withHistory > 0
                  ? 'Rather have a fresh price than the ones above?'
                  : 'No past price for this item. Get one:'}
            </span>
            {onSendRfq ? (
              /*
                This now WRITES before it navigates — it puts the item on the enquiry, without
                which no RFQ can carry it (`revisionQuoteLine.ts`'s `planRfqSourcing`). A write on
                this bench costs the best part of a second, so it says so rather than sitting dead
                under a disabled attribute.
              */
              <Button size="sm" disabled={busy} onClick={onSendRfq}>
                {busy ? 'Adding to the enquiry…' : '✉ Send an RFQ'}
              </Button>
            ) : null}
            {onRecordQuotation ? (
              <Button size="sm" variant="primary" disabled={busy} onClick={onRecordQuotation}>
                ✎ Record a price they gave
              </Button>
            ) : null}
          </div>
        ) : null}

        {options.map((option) => (
          <div
            key={option.supplier}
            className="srow"
            role="button"
            tabIndex={0}
            onClick={() => !busy && onPick(option.supplier)}
            onKeyDown={(e) => {
              if (!busy && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault()
                onPick(option.supplier)
              }
            }}
          >
            <div>
              <div className="nm">{option.supplier}</div>
              <div className="cd">
                {option.hasHistory ? (
                  <>
                    <Badge tone="ok">history</Badge>{' '}
                    <span style={{ color: 'var(--ok)', fontWeight: 700 }}>
                      last <Money value={option.lastPrice} />
                    </span>{' '}
                    <span style={{ color: 'var(--muted)' }}>
                      · RFQ'd on {option.ref}, {option.date ? formatDate(option.date) : ''}
                    </span>
                  </>
                ) : (
                  <>
                    <Badge tone="muted">no history</Badge>{' '}
                    <span style={{ color: 'var(--muted)' }}>connected supplier — never quoted this item</span>
                  </>
                )}
                {/* `stock` is `null` when the master has no figure at all (the normal case
                    against Quoflow — see `SupplierOption.stock`'s own comment) — nothing renders
                    rather than a fabricated "none on hand", which used to assert a stock-out this
                    mock never actually observed. */}
                {option.stock !== null ? (
                  <>
                    <br />
                    {option.stock > 0 ? (
                      <span style={{ color: 'var(--ok)' }}>on hand {option.stock}</span>
                    ) : (
                      <span style={{ color: 'var(--danger)' }}>none on hand</span>
                    )}
                  </>
                ) : null}
              </div>
            </div>
            <div style={{ alignSelf: 'center' }}>
              <Button
                size="sm"
                variant={option.hasHistory ? 'primary' : 'default'}
                disabled={busy}
                onClick={(e) => {
                  e.stopPropagation()
                  onPick(option.supplier)
                }}
              >
                {option.hasHistory ? 'Use — have price' : 'Request RFQ'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  )
}
